import React, { useCallback, useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { Modal, Spinner, Table } from "react-bootstrap";
import { Paper } from "@material-ui/core";
import SVG from "react-inlinesvg";
import { baseUrl } from "../../../services/config";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { PremissionChecker } from "../../../services/PremissionChecker";
import { useSelector } from "react-redux";

function DefineCRMActivityType() {
  const alert = useAlert();
  const user = useSelector((state) => state.user.user);
  const authtoken = localStorage.getItem("userToken");
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState(null);
  const [name, setName] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const showErrors = useCallback(
    (data, fallback) => {
      const message = data?.metadata?.message;
      if (message && typeof message === "object") {
        Object.values(message).forEach((item) => alert.error(item));
      } else {
        alert.error(message || fallback);
      }
    },
    [alert],
  );

  const getActivityTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/crm-activity-types`, {
        headers: { Authorization: `Bearer ${authtoken}` },
      });
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to load CRM activity types");
        return;
      }
      setActivityTypes(data?.payload || []);
    } catch (error) {
      alert.error("Unable to load CRM activity types");
    } finally {
      setLoading(false);
    }
  }, [alert, authtoken, showErrors]);

  useEffect(() => {
    getActivityTypes();
  }, [getActivityTypes]);

  const closeModal = () => {
    setModal(null);
    setName("");
    setSelectedId(null);
  };

  const saveActivityType = async () => {
    if (!name.trim()) {
      alert.error("Activity Type Name Is Required");
      return;
    }

    const isEditing = modal === "edit";
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-activity-types${isEditing ? `/${selectedId}` : ""}`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`,
          },
          body: JSON.stringify({ name: name.trim() }),
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(
          data,
          `Unable to ${isEditing ? "update" : "add"} activity type`,
        );
        return;
      }
      alert.success(
        `Activity type successfully ${isEditing ? "updated" : "added"}`,
      );
      closeModal();
      getActivityTypes();
    } catch (error) {
      alert.error(`Unable to ${isEditing ? "update" : "add"} activity type`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteActivityType = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-activity-types/${selectedId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authtoken}` },
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to delete activity type");
        return;
      }
      alert.success(data?.payload || "Activity type successfully deleted");
      closeModal();
      getActivityTypes();
    } catch (error) {
      alert.error("Unable to delete activity type");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      {PremissionChecker(user, "crm_activity_type.create") && (
        <button
          className="btn btn-primary mr-2"
          onClick={() => setModal("add")}
        >
          Add CRM Activity Type
        </button>
      )}

      <Table className="mt-5">
        <thead>
          <tr className="text-center">
            <th>ID</th>
            <th>Activity Type Name</th>
            {PremissionChecker(user, "crm_activity_type.update") && (
              <th>Update</th>
            )}
            {PremissionChecker(user, "crm_activity_type.delete") && (
              <th>Delete</th>
            )}
          </tr>
        </thead>
        <tbody>
          {activityTypes.map((activityType) => (
            <tr className="text-center" key={activityType.id}>
              <td>{activityType.id}</td>
              <td>{activityType.name}</td>
              {PremissionChecker(user, "crm_activity_type.update") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-3"
                    onClick={() => {
                      setSelectedId(activityType.id);
                      setName(activityType.name || "");
                      setModal("edit");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Write.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
              {PremissionChecker(user, "crm_activity_type.delete") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-danger btn-sm mx-3"
                    onClick={() => {
                      setSelectedId(activityType.id);
                      setModal("delete");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-danger">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Delete-user.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {loading && (
        <div className="row justify-content-center">
          <Spinner animation="border" />
        </div>
      )}

      <Modal
        show={modal === "add" || modal === "edit"}
        onHide={closeModal}
        size="md"
      >
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            {modal === "edit" ? "Update" : "Add New"} CRM Activity Type
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-control-label">
            <b>
              Enter Activity Type Name <span style={{ color: "red" }}>*</span>
            </b>
          </label>
          <input
            className="form-control"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Enter Activity Type Name"
          />
        </Modal.Body>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button
              className="btn btn-primary w-100px"
              onClick={saveActivityType}
            >
              {modal === "edit" ? "Update" : "Add"}
            </button>
          )}
          <button className="btn btn-danger" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={modal === "delete"} onHide={closeModal}>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Are you sure you want to delete this CRM activity type?
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn button_blue" onClick={deleteActivityType}>
              Delete
            </button>
          )}
          <button className="btn btn-danger btn_width" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
}

export default DefineCRMActivityType;
